/* sugar:assets/sticky-header.js@2.1.8 */
/**
 * Sticky Header Custom Element
 * Handles hide-on-scroll-down / reveal-on-scroll-up behavior.
 * Loaded as a deferred script from header section.
 */
(function () {
  if (customElements.get('sticky-header')) return;

  function isElementVisible(element) {
    if (!element) return false;
    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  // A header set to overlay the first section has a section box of no height; the header itself
  // is the part that takes room on screen.
  function headerBox(header) {
    return (header && header.querySelector('.sugar-header--overlay')) || header;
  }

  function collectTopChromeMetrics(headerElement) {
    const header = headerElement || document.querySelector('.section-header');
    const box = headerBox(header);
    const headerHeight = isElementVisible(box) ? Math.round(box.getBoundingClientRect().height) : 0;

    let announcementBarsHeight = 0;
    const announcementBars = document.querySelectorAll('.announcement-bar-section');
    announcementBars.forEach((bar) => {
      if (!isElementVisible(bar)) return;
      // Only bars that sit above the header are part of the top chrome the sticky
      // header must clear. A bar rendered below the header (e.g. one placed in the
      // template) scrolls away with the page, so counting it would push the pinned
      // header down and leave a gap beneath the always-sticky bar above it.
      if (header && !(header.compareDocumentPosition(bar) & Node.DOCUMENT_POSITION_PRECEDING)) return;
      announcementBarsHeight += Math.round(bar.getBoundingClientRect().height);
    });

    return {
      headerHeight,
      announcementBarsHeight,
      topChromeHeight: headerHeight + announcementBarsHeight
    };
  }

  // A property written on <html> restyles the whole page, so a value that has
  // not changed is left alone.
  function setRootVar(name, value) {
    const root = document.documentElement.style;
    if (root.getPropertyValue(name) === value) return;
    root.setProperty(name, value);
  }

  function syncTopChromeCssVars(headerElement) {
    const metrics = collectTopChromeMetrics(headerElement);
    setRootVar('--header-height', `${metrics.headerHeight}px`);
    setRootVar('--announcement-bars-height', `${metrics.announcementBarsHeight}px`);
    setRootVar('--top-chrome-height', `${metrics.topChromeHeight}px`);
    return metrics;
  }

  window.sugarThemeSyncTopChrome = function () {
    return syncTopChromeCssVars(document.querySelector('.section-header'));
  };

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (window.__swOk === false) return;
      this.header = document.querySelector('.section-header');
      if (!this.header) return;

      // "…, desktop only" types stick from the header's Desktop layout from width and scroll away
      // with the page below it.
      const type = this.getAttribute('data-sticky-type') || '';
      this.desktopOnly = /-desktop$/.test(type);
      const baseType = type.replace(/-desktop$/, '');
      this.headerIsAlwaysSticky = baseType === 'always';
      // Pinned through the first section: stays in view until the first section after the header has
      // scrolled by, then hides on the way down and returns on the way up.
      this.pinFirst = baseType === 'after-first';
      this.pinUntil = 0;
      this.headerBounds = {};
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      this.onLayoutChange = this.setHeaderHeight.bind(this);

      this.mobileMediaQuery = window.matchMedia(`(max-width: ${this.dataset.desktopFrom || 990}px)`);
      this.offWidth = this.desktopOnly && this.mobileMediaQuery.matches;
      this.setHeaderHeight();
      this.mobileMediaQuery.addEventListener('change', this.onLayoutChange);
      if (this.desktopOnly) {
        this.onWidthChange = this.syncDesktopOnly.bind(this);
        this.mobileMediaQuery.addEventListener('change', this.onWidthChange);
      }
      window.addEventListener('resize', this.onLayoutChange, { passive: true });
      document.addEventListener('shopify:section:load', this.onLayoutChange);
      document.addEventListener('shopify:section:unload', this.onLayoutChange);
      document.addEventListener('shopify:section:reorder', this.onLayoutChange);
      this.setupTopChromeObservers();

      if (this.headerIsAlwaysSticky && !this.offWidth) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.createObserver();
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    }

    setHeaderHeight() {
      const metrics = syncTopChromeCssVars(this.header);
      if (this.pinFirst) this.pinUntil = this.firstSectionBottom();
      if (this.headerIsAlwaysSticky && !this.offWidth) {
        this.setHeaderStickyOffset(metrics.headerHeight);
      }
    }

    // A desktop-only header crossing its breakpoint: below it the header lets go and scrolls with the
    // page; above it the header picks its sticky state back up from the current scroll position.
    syncDesktopOnly() {
      this.offWidth = this.mobileMediaQuery.matches;
      if (this.offWidth) {
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate', 'scrolled-past-header');
        this.setHeaderStickyOffset(0);
        return;
      }
      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
        this.setHeaderHeight();
      }
      this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    }

    setHeaderStickyOffset(offset) {
      setRootVar('--header-sticky-offset', `${offset}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
      if (this.mobileMediaQuery) {
        this.mobileMediaQuery.removeEventListener('change', this.onLayoutChange);
        if (this.onWidthChange) this.mobileMediaQuery.removeEventListener('change', this.onWidthChange);
      }
      window.removeEventListener('resize', this.onLayoutChange);
      document.removeEventListener('shopify:section:load', this.onLayoutChange);
      document.removeEventListener('shopify:section:unload', this.onLayoutChange);
      document.removeEventListener('shopify:section:reorder', this.onLayoutChange);
      if (this.topChromeResizeObserver) {
        this.topChromeResizeObserver.disconnect();
      }
      if (this.topChromeBodyObserver) {
        this.topChromeBodyObserver.disconnect();
      }
    }

    setupTopChromeObservers() {
      if (typeof ResizeObserver !== 'undefined') {
        this.topChromeResizeObserver = new ResizeObserver(() => this.setHeaderHeight());
        if (this.header) {
          this.topChromeResizeObserver.observe(headerBox(this.header));
        }
        document.querySelectorAll('.announcement-bar-section').forEach((bar) => {
          this.topChromeResizeObserver.observe(bar);
        });
      }

      if (typeof MutationObserver !== 'undefined' && document.body) {
        this.topChromeBodyObserver = new MutationObserver((mutations) => {
          for (let i = 0; i < mutations.length; i++) {
            if (mutations[i].attributeName === 'data-transparent-header-active') {
              this.setHeaderHeight();
              break;
            }
          }
        });
        this.topChromeBodyObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['data-transparent-header-active']
        });
      }
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });
      observer.observe(headerBox(this.header));
    }

    // The first section after the header (a template's own header) or the first one in the main
    // content (the store's header group), less the header's own height: where pinning ends.
    firstSectionBottom() {
      const isContent = (el) => el.classList.contains('shopify-section') && el.offsetHeight > 0 &&
        !el.querySelector('.announcement-bar-section, [data-ab-root]') && !el.classList.contains('announcement-bar-section');
      let el = this.header.nextElementSibling;
      while (el && !isContent(el)) el = el.nextElementSibling;
      if (!el) el = Array.from(document.querySelectorAll('main .shopify-section')).find((s) => s !== this.header && isContent(s));
      if (!el) return 0;
      return el.getBoundingClientRect().bottom + window.scrollY - headerBox(this.header).offsetHeight;
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
      if (this.offWidth) {
        this.currentScrollTop = scrollTop;
        return;
      }

      if (this.pinFirst && scrollTop <= this.pinUntil) {
        this.header.classList.toggle('scrolled-past-header', scrollTop > this.headerBounds.bottom);
        if (!this.header.classList.contains('shopify-section-header-sticky') || this.header.classList.contains('shopify-section-header-hidden')) {
          requestAnimationFrame(this.reveal.bind(this));
        }
        this.currentScrollTop = scrollTop;
        return;
      }

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        // An open menu holds the header in place; the scroll position is still recorded below, so
        // scrolling back up reveals the header once the menu has closed.
        if (!this.preventHide && !this.hoverPanelOpen()) requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    // A hover menu's panel shows while it is pointed at or opened from the keyboard.
    hoverPanelOpen() {
      return !!this.querySelector('header-menu[data-hover-open]:hover:not([data-dm-closed]), header-menu[data-dm-open]');
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
      this.setHeaderStickyOffset(0);
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      this.setHeaderStickyOffset(headerBox(this.header).offsetHeight);
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove(
        'shopify-section-header-hidden',
        'shopify-section-header-sticky',
        'animate'
      );
      this.setHeaderStickyOffset(0);
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => {
        if (typeof disclosure.close === 'function') disclosure.close();
      });
    }

    closeSearchModal() {
      const dialog = this.header.querySelector('.search-dialog');
      if (dialog && dialog.open) {
        dialog.close();
      }
    }
  }

  // Register after DOMContentLoaded so desktop menus are injected and header has final dimensions.
  // When the class is defined, the existing <sticky-header> element in the DOM gets upgraded
  // immediately and connectedCallback fires with the correct header height.
  function register() {
    customElements.define('sticky-header', StickyHeader);
  }

  if (document.readyState === 'complete') {
    register();
  } else {
    document.addEventListener('DOMContentLoaded', register);
  }
})();
